import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { infiniteListPageSize } from "../../../../models/common/webConsts";
import * as taskService from "../../../../models/services/taskService";
import { ISessionState } from "../../../../models/session/sessionState";
import { ITaskTypeListItem } from "../../../../models/taskType/taskTypeListItem";
import { ActionContainer } from "../../../framework/ActionContainer";
import { DialogSettingsCard } from "../../../framework/Container/DialogSettingsCard";
import { List } from "../../../framework/List/List";
import { ListIconButtonsItem } from "../../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "../../../framework/List/ListLinkValue";
import { ListSearchFilter } from "../../../framework/List/ListSearchFilter";
import { TaskTypeDialog } from "../../TaskTypeDialog";
import { Badge, Box, Tooltip, Typography } from "@mui/material";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";
import { TaskTypeNewDialog } from "../../TaskTypeNewDialog";
import { IListCardData } from "source/components/framework/List/ListLine";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";

export interface ITaskTypeGridProp {
    companyId: string;
    sessionState: ISessionState;
}

interface ITaskTypeGridState {
    // List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: ITaskTypeListItem[];
    selectedId: string;
    checkedIds: string[];

    // New Form Dialog
    showNewFormDialog: boolean;

    // Edit Dialog
    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
    editDialogAddToCompany: boolean;
}

export class TaskTypeGrid extends React.Component<ITaskTypeGridProp, ITaskTypeGridState> {
    constructor(props: ITaskTypeGridProp) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: "activeState",
            sortOrderIsAsc: false,
            items: [],
            selectedId: null,
            checkedIds: [],
            showNewFormDialog: false,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogAddToCompany: false
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => taskService.getTaskTypeListItems(this.props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc, null), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.state;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        const editDialogStateChange = setEditDialogData
            ? {
                editDialogId: moveNext && selectedItem ? selectedItem.id : "",
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < items.items.length - 1 || result.hasMore,
                editDialogAddToCompany: false
            }
            : null;
        this.setState({
            ...editDialogStateChange,
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
        });
    };

    refreshList = async (setEducationDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEducationDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: ITaskTypeGridProp, prevState: ITaskTypeGridState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    //#region Edit Dialog
    handleShowEditDialog = (taskTypeId: string, addToCompany: boolean) => {
        const state = this.state;
        const isNew = taskTypeId === Base.emptyGuid || addToCompany;
        const index = isNew ? 0 : state.items.findIndex(i => i.id === taskTypeId);
        if (index < 0) return;
        this.setState({
            showNewFormDialog: false,
            showEditDialog: true,
            editDialogId: taskTypeId,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < state.items.length - 1 || state.hasMore,
            editDialogAddToCompany: addToCompany
        });
    };

    handleAddNew = () => {
        this.handleShowEditDialog(Base.emptyGuid, false);
    };

    handleShowNewFormDialogOpen = () => {
        this.setState({
            showNewFormDialog: true,
        });
    };

    handleShowNewFormDialogClose = () => {
        this.setState({
            showNewFormDialog: false,
        });
    };

    handleOpenLine = (id: string) => {
        this.handleShowEditDialog(id, false);
    };

    handleEditDialogOk = async () => {
        this.refreshList(false);
    };

    handleEditDialogCancel = () => {
        this.setState({
            showEditDialog: false
        });
        this.refreshList(false);
    };
    //#endregion Edit Dialog

    getRemoveSaveData = (props: ITaskTypeGridProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("ids", ids);
        return result;
    };

    remove = async (ids: string[]): Promise<ApiSuccess> => {
        const props = this.props;
        if (ids.length < 1) return;
        const saveData = this.getRemoveSaveData(props, ids);
        // Call server
        const result = await AppUtils.callService(() => taskService.removeTaskTypes(saveData.obj));
        if (!result) return null;
        this.refreshList();
        return result;
    };

    handleRemove = async () => {
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedTaskTypes
            : String.format(Translations.DoYouReallyWantToRemoveFormParameter, selectedItems[0].code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            this.remove(selectedIds);
        }
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0) return;
        if (index >= state.items.length) {
            if (!state.hasMore) return;
            this.searchItems(state.pageSize, state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true);
        } else {
            this.setState({
                selectedId: state.items[index].id,
                editDialogId: state.items[index].id,
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < state.items.length - 1 || state.hasMore,
            });
        }
    };

    getItemCardData = (item: ITaskTypeListItem): IListCardData => {
        return {
            sm: 4,
            rows: [
                {
                    content:
                        <Box sx={{ marginBottom: "-10px", width: "100%" }}>
                            <Box display="flex" justifyContent="space-between" width="100%">
                                <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                {(item.usageAmount > 0 || item.activeState > 0) &&
                                    <Box sx={{ marginRight: "-8px", display: "flex" }} >
                                        {item.usageAmount > 0 &&
                                            <Box sx={{ marginLeft: "10px" }} >
                                                <Tooltip title={String.format(Translations.UsedInParameterTasks, item.usageAmount)} placement="top">
                                                    <Badge color="secondary" badgeContent={item.usageAmount}></Badge>
                                                </Tooltip>
                                            </Box>
                                        }
                                        {item.activeState > 0 &&
                                            <Box sx={{ marginLeft: "10px" }} >
                                                <ListStateIconSuccess tooltip={Translations.InUse} />
                                            </Box>
                                        }
                                    </Box>
                                }
                            </Box>
                            <Box sx={{ height: "40px", overflow: "hidden", textOverflow: "ellipsis" }} title={item.comment}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>{item.comment}</Typography>
                            </Box>
                        </Box>
                }
            ],
            id: item.id
        };
    };

    getItemCardDatas = (): IListCardData[] => {
        const state = this.state;
        const result = state.items.filter(i => i.activeState).map(i => this.getItemCardData(i));
        const notInUseItems = state.items.filter(i => !i.activeState);
        if (notInUseItems.length > 0) {
            result.push({ id: Translations.NotInUse, subTitle: Translations.NotInUse, rows: [] });
            result.push(...notInUseItems.map(i => this.getItemCardData(i)));
        }
        return result;
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.companyId ? Translations.TaskTypes : Translations.DefaultTaskTypes}
                    info={props.companyId ? Translations.CompanyTaskTypesInstruction : null}
                    cardContent={
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddTaskType}
                                    removeTooltip={Translations.RemoveTaskType}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={!props.companyId ? this.handleAddNew : this.handleShowNewFormDialogOpen}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                    }
                    content={
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={state.hasMore}
                            multiSelect={false}
                            globalLoadingInUse={true}
                            height={props.companyId ? "calc(100vh - 432px)" : "calc(100vh - 362px)"}
                            cards={this.getItemCardDatas()}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpenLine}
                        />
                    }
                />
                {state.showEditDialog && (
                    <TaskTypeDialog
                        id={state.editDialogId}
                        sessionState={props.sessionState}
                        companyId={props.companyId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        addToCompany={state.editDialogAddToCompany}
                        onSaved={this.handleEditDialogOk}
                        onClose={this.handleEditDialogCancel}
                        onRemove={this.remove}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                )}
                {state.showNewFormDialog && (
                    <TaskTypeNewDialog
                        companyId={props.companyId}
                        onOk={(taskTypeId: string) => this.handleShowEditDialog(taskTypeId, true)}
                        onCancel={this.handleShowNewFormDialogClose}
                    />
                )}
            </>
        );
    }
};
